/**
 * Web Search Utility Functions
 * Shared utilities for web search adapters
 */

import { getBackendUrl as getRuntimeBackendUrl } from '@/config/runtimeConfig';
import { getBackendAuth, getBackendHeaders } from '../backendAuth';
import { reportError } from '@/services/errorTelemetry';

export type WebSearchProviderName =
  | 'tavily'
  | 'brave'
  | 'bing'
  | 'google'
  | 'serpapi';

// Per-provider hard caps on query length. Conservative values that match
// each provider's documented limit (or stricter where the limit isn't
// publicly documented but the API rejects long queries in practice).
//   - Tavily: 400 (HTTP 400 "Query is too long. Max query length is 400")
//   - Brave:  400 (same observed behavior; their docs say "up to 400 chars")
//   - Bing v7: 1500 chars
//   - Google CSE: 2048 chars
//   - SerpAPI: 2048 (matches Google upstream)
export const MAX_QUERY_LENGTH_BY_PROVIDER: Record<WebSearchProviderName, number> = {
  tavily: 400,
  brave: 400,
  bing: 1500,
  google: 2048,
  serpapi: 2048,
};

/**
 * Module-level current trace id.
 *
 * The frontend is single-threaded and only one chat turn is in flight at a
 * time, so a module-level variable is safe here.  App.tsx sets this at the
 * very start of handleSendMessage and clears it in the finally block.
 */
let currentTraceId: string | undefined;

/**
 * Set (or clear) the trace id for the currently in-flight chat turn.
 * Called by App.tsx handleSendMessage via try/finally.
 */
export function setCurrentTraceId(id: string | undefined): void {
  currentTraceId = id;
}

/**
 * Get the trace id for the currently in-flight chat turn, or undefined when
 * no turn is active. Used by other service modules to append X-Trace-Id to
 * their backend requests.
 */
export function getCurrentTraceId(): string | undefined {
  return currentTraceId;
}

/**
 * Get a bearer token for the /api/web-search backend proxy.
 *
 * Throws when neither a DB JWT nor an Entra access token is available
 * — every web-search adapter has historically expected a usable token,
 * so we preserve that contract instead of letting the call fire
 * unauthenticated.
 */
export async function getAuthToken(): Promise<string> {
  const auth = await getBackendAuth();
  if (!auth) {
    throw new Error('Authentication token not found. Please login first.');
  }
  return auth.token;
}

/**
 * Get backend URL with runtime configuration support
 * Priority: Runtime config > Build-time env vars > Fallback
 */
export function getBackendUrl(): string {
  return getRuntimeBackendUrl();
}

/**
 * Trim a web-search query to the provider's hard limit on a word
 * boundary. Falls back to a hard slice if no whitespace is present in
 * the leading window. Emits a `[Web Search Telemetry] truncated` console
 * warn when truncation occurs so the event is visible in browser logs
 * and front-end error monitors.
 */
export function prepareWebSearchQuery(
  query: string,
  provider: WebSearchProviderName
): string {
  const trimmed = query.trim();
  const max = MAX_QUERY_LENGTH_BY_PROVIDER[provider];
  if (trimmed.length <= max) {
    return trimmed;
  }

  const window = trimmed.slice(0, max);
  const lastSpace = window.lastIndexOf(' ');
  const result =
    lastSpace > Math.floor(max * 0.6) ? window.slice(0, lastSpace) : window;

  // eslint-disable-next-line no-console
  console.warn(
    `[Web Search Telemetry] truncated provider=${provider} original=${trimmed.length} truncated=${result.length} cap=${max}`
  );

  // Surface on the Errors dashboard. The search will still succeed against
  // the truncated query, so this is a 'warning' (the user got results but
  // not the ones they typed for). Backend's search_events table also flags
  // these rows with search_mode='truncated' for the Web Search analytics
  // page, but ops watching Errors needs the same signal.
  reportError('Web search query truncated to provider cap', {
    area: 'web-search',
    subtype: 'query-truncated',
    provider,
    originalLength: trimmed.length,
    truncatedLength: result.length,
    cap: max,
    traceId: currentTraceId,
  }, 'warning');

  return result;
}

/**
 * Get auth headers for /api/web-search backend requests.
 *
 * Delegates to the shared backendAuth helper so SSO callers always
 * forward X-User-Email / X-User-Username (the backend needs them to
 * keep the users table out of the 'unknown' fallback state).
 *
 * When a chat turn is in flight (currentTraceId is set), the header
 * X-Trace-Id is included so the backend can correlate web-search,
 * AI-search, and embedding proxy calls to the parent trace.
 *
 * Throws — same contract as `getAuthToken` — when no token is available.
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const auth = await getBackendAuth();
  if (!auth) {
    throw new Error('Authentication token not found. Please login first.');
  }
  const headers = await getBackendHeaders();
  if (currentTraceId) {
    return { ...headers, 'X-Trace-Id': currentTraceId };
  }
  return headers;
}
